// src/components/report/blocksPdf/renderLexicalTextarea.js
import htmlToPdfMake from "html-to-pdfmake";
import { paragraphStyle as defaultParagraphStyle, listStyle, bottomMarginItemGlobal } from "../../utils/pdfConstants";
import { cleanEmptyParagraphs } from "../../utils/cleanEmptyParagraphs";


export function renderLexicalTextarea({ data, content, tableCounter, paragraphStyle = defaultParagraphStyle }) {
  let htmlContent = data?.html || "";
  htmlContent = htmlContent.trim();

  // Remove empty paragraphs first
  htmlContent = cleanEmptyParagraphs(htmlContent);

  /**
   * Sanitize Lexical HTML and convert DOUBLE newlines into separate paragraphs
   */
  function sanitizeAndSplitParagraphs(html) {
    // Normalize Windows newlines
    html = html.replace(/\r\n/g, "\n");

    // Convert \n\n anywhere within span text (start or middle) into paragraph breaks.
    // Must run before the leading-newline strip so the double newline isn't erased.
    html = html.replace(
      /<p([^>]*)>([\s\S]*?)<\/p>/gi,
      (match, pAttrs, inner) => {
        if (!/\n\n/.test(inner)) return match;
        const result = inner.replace(
          /(<span[^>]*>)([\s\S]*?)(<\/span>)/g,
          (spanMatch, open, text, close) => {
            if (!/\n\n/.test(text)) return spanMatch;
            const parts = text.split(/\n\n+/).filter(p => p.trim() !== "");
            if (parts.length === 0) return "";
            if (parts.length === 1) return `${open}${parts[0]}${close}`;
            return parts.map(p => `${open}${p}${close}`).join(`</p><p${pAttrs}>`);
          }
        );
        return `<p${pAttrs}>${result}</p>`;
      }
    );

    // Remove leading/trailing newlines inside tags
    html = html
      .replace(/(<(p|span)[^>]*>)\n+/gi, "$1")
      .replace(/\n+(<\/(p|span)>)/gi, "$1");

    // Forbid 3+ newlines
    html = html.replace(/\n{3,}/g, "\n\n");

    // Split paragraphs on DOUBLE newline inside spans
    html = html.replace(
      /<\/span><\/p>\s*<p([^>]*)>\s*<span([^>]*)>([\s\S]*?)<\/span>\s*<\/p>/gi,
      (match, pAttrs, spanAttrs, text) => {
        const parts = text.split(/\n\n+/).map(t => t.trim()).filter(Boolean);
        return parts
          .map(part => `<p${pAttrs}><span${spanAttrs}>${part}</span></p>`)
          .join("");
      }
    );

    return html.trim();
  }

  htmlContent = sanitizeAndSplitParagraphs(htmlContent);

  if (!htmlContent) {
    content.push({ text: "", style: "paragraph", margin: [0, 0, 0, 0] });
    return;
  }

  // --- Split HTML into segments: normal HTML, checklist <ul>, and <table> blocks ---
  const segments = [];
  // Match checklist ULs or tables — whichever comes first
  const blockRe = /(<ul[^>]*>[\s\S]*?<\/ul>|<table[^>]*>[\s\S]*?<\/table>)/gi;
  let lastPos = 0;
  let m;
  while ((m = blockRe.exec(htmlContent)) !== null) {
    const block = m[0];
    if (m.index > lastPos) {
      segments.push({ type: 'html', content: htmlContent.slice(lastPos, m.index) });
    }
    if (/^<table/i.test(block)) {
      segments.push({ type: 'table', html: block });
    } else {
      // UL block — check if it's a checklist
      const inner = block.replace(/^<ul[^>]*>|<\/ul>$/gi, '');
      if (/<li[^>]*role=["']checkbox["']/i.test(inner)) {
        const items = [];
        inner.replace(/<li[^>]*role=["']checkbox["'][^>]*>([\s\S]*?)<\/li>/gi, (_, c) => {
          items.push(c.replace(/<[^>]+>/g, '').trim());
        });
        if (items.length) segments.push({ type: 'checklist', items });
      } else {
        segments.push({ type: 'html', content: block });
      }
    }
    lastPos = m.index + m[0].length;
  }
  if (lastPos < htmlContent.length) {
    const tail = htmlContent.slice(lastPos).trim();
    if (tail) segments.push({ type: 'html', content: tail });
  }
  if (segments.length === 0) {
    segments.push({ type: 'html', content: htmlContent });
  }

  // --- Shared html-to-pdfmake options ---
  const htmlOpts = {
    defaultStyles: {
      p: { ...paragraphStyle },
      span: {},
      b: { bold: true },
      strong: { bold: true },
      ul: { ...listStyle },
      ol: { ...listStyle },
      li: { margin: [0, 0, 0, 0] },
    },
  };

  // --- Table HTML → pdfmake object (green header, alternating rows, centered) ---
  const GREEN = '#04aa00';
  const TF = { font: 'Lato', fontSize: 10 };
  let lexicalTableIdx = 0;

  // Browser DOM normalises colors to rgb() — pdfmake only accepts hex
  function toHex(color) {
    if (!color) return null;
    if (color.startsWith('#')) return color;
    const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return color;
    return '#' + [m[1], m[2], m[3]].map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
  }

  function domToRuns(node, fmts) {
    if (node.nodeType === 3) {
      const t = node.textContent;
      if (!t) return [];
      const r = { text: t, ...TF };
      if (fmts.bold) r.bold = true;
      if (fmts.italic) r.italics = true;
      if (fmts.color) r.color = fmts.color;
      return [r];
    }
    if (node.nodeType !== 1) return [];
    const tag = node.tagName;
    const f = { ...fmts };
    if (tag === 'STRONG' || tag === 'B') f.bold = true;
    if (tag === 'EM' || tag === 'I') f.italic = true;
    if (node.style?.color) f.color = toHex(node.style.color);
    return Array.from(node.childNodes).flatMap(c => domToRuns(c, f));
  }

  function cellToPdf(cell, isHeader) {
    const runs = Array.from(cell.childNodes)
      .flatMap(c => domToRuns(c, {}))
      .filter(r => r.text.trim());
    const textVal = runs.length === 0 ? '' : runs.length === 1 ? runs[0] : runs;
    if (isHeader) {
      return { text: textVal, ...TF, bold: true, color: 'white', fillColor: GREEN, alignment: 'center', margin: [0, 2, 0, 2] };
    }
    return { text: textVal, ...TF, alignment: 'center' };
  }

  function makeTableSection(tableHtml) {
    const doc = new DOMParser().parseFromString(tableHtml, 'text/html');
    const rows = Array.from(doc.querySelectorAll('tr'));
    if (!rows.length) return null;
    const body = rows.map(tr =>
      Array.from(tr.querySelectorAll('th, td')).map(cell => cellToPdf(cell, cell.tagName === 'TH'))
    ).filter(row => row.length);
    if (!body.length) return null;
    const colCount = Math.max(...body.map(r => r.length));

    const tblNum = tableCounter ? ++tableCounter.value : null;
    const caption = (data.tableCaptions || [])[lexicalTableIdx] || '';
    lexicalTableIdx++;

    return [
      {
        table: {
          headerRows: rows[0]?.querySelector('th') ? 1 : 0,
          widths: Array(colCount).fill('*'),
          body,
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#adb5bd',
          vLineColor: () => '#adb5bd',
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 3,
          paddingBottom: () => 3,
          fillColor: (row) => row > 0 && row % 2 === 1 ? '#f8f9fa' : null,
        },
        margin: [0, 4, 0, bottomMarginItemGlobal],
      },
      ...(tblNum !== null ? [{
        text: [{ text: `Taulukko ${tblNum}. `, bold: true }, { text: caption, bold: false }],
        font: 'Lato', fontSize: 9,
        margin: [0, 0, 0, bottomMarginItemGlobal],
      }] : []),
    ];
  }

  function fixText(str) {
    if (/^\s*$/.test(str)) return str;
    return str
      .replace(/^\n+/, "")
      .replace(/\n+$/, "")
      .replace(/\n{2,}/g, "\n")
      .replace(/⁰/g, "°");
  }

  function postProcessPdfNodes(node) {
    if (Array.isArray(node)) { node.forEach(postProcessPdfNodes); return; }
    if (!node || typeof node !== "object") return;
    if (typeof node.text === "string") {
      node.text = fixText(node.text);
    } else if (Array.isArray(node.text)) {
      node.text.forEach(postProcessPdfNodes);
    }
    ["stack", "columns", "ul", "ol"].forEach(key => {
      if (Array.isArray(node[key])) node[key].forEach(postProcessPdfNodes);
    });
  }

  // SVG checkmark — explicit width/height tells pdfmake the column size upfront,
  // avoiding the canvas layout height issue.
  const ICON_SIZE = paragraphStyle.fontSize; // match font size, not full line height
  const CHECKMARK_SVG = `<svg viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
    <polyline points="1,7 4.5,11 11,3" stroke="#198754" stroke-width="2.2"
      fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

  function makeChecklistSection(items) {
    return {
      stack: items.map(text => ({
        columns: [
          {
            svg: CHECKMARK_SVG,
            width: ICON_SIZE,
            height: ICON_SIZE,
          },
          {
            text,
            font: paragraphStyle.font,
            fontSize: paragraphStyle.fontSize,
            lineHeight: paragraphStyle.lineHeight,
            alignment: 'left',
          },
        ],
        columnGap: 8,
        margin: [0, 0, 0, 2],
      })),
      margin: [listStyle.margin[0], 0, 0, paragraphStyle.margin[3] - 2],
    };
  }

  // --- Process each segment ---
  for (const seg of segments) {
    if (seg.type === 'html') {
      const trimmed = seg.content.trim();
      if (!trimmed) continue;
      const nodes = htmlToPdfMake(trimmed, htmlOpts);
      postProcessPdfNodes(nodes);
      if (Array.isArray(nodes)) content.push(...nodes);
      else content.push(nodes);
    } else if (seg.type === 'checklist') {
      content.push(makeChecklistSection(seg.items));
    } else if (seg.type === 'table') {
      const items = makeTableSection(seg.html);
      if (items) content.push(...items);
    }
  }
}
